/**
 * Patient lifecycle service (Phase 6): Admit, hold/discharge read-only context
 * (future-shift count, open PAs, unsigned POC certifications).
 *
 * Status changes themselves (hold, resume, discharge, readmit) go through
 * services/admin/patient.service.ts because they use PUT /patients/{id}.
 */

import { apiClient } from '../api-client';
import { capReachedMessage } from '../../utils/subscription';
import { describeApiError } from '../../utils/apiErrors';
import { EvvReconciliationService, PatientPendingEvv } from '../evv/reconciliation.service';
import { ClaimsService } from '../billing/claims.service';
import { ApiError, ApiResponse } from '../../dtos/common/api.dto';
import type { AdmitPatientResponse, AdmitWarning } from '../../dtos/admin/patient.dto';
import { AuthorizationsService } from './authorizations.service';
import { PAStatus } from '../../dtos/patientmr/authorizations.dto';
import type { Authorization } from '../../dtos/patientmr/authorizations.dto';
import { POCCertificationsService } from '../pocCertifications.service';
import { listScheduleEntriesByPatient } from '../scheduling';
import type { ScheduleEntry } from '../scheduling';

/** PA statuses that are still open (not DENIED / EXPIRED / EXHAUSTED). Legacy lowercase included. */
const OPEN_PA_STATUSES = new Set<string>([
  PAStatus.DRAFT,
  PAStatus.SUBMITTED,
  PAStatus.PENDED,
  PAStatus.APPROVED,
  'ACTIVE',
  'PENDING',
]);

const UNSIGNED_POC_STATUSES = new Set<string>(['draft', 'pending_signature']);

/**
 * How far ahead the future-shift count looks. Phase 9A materializes recurring shifts up to
 * today + 60 days (R-118: "all future materialized shifts"); the API allows up to 62 days.
 */
const FUTURE_SHIFT_WINDOW_DAYS = 61;

export interface OpenPriorAuthSummary {
  id: string;
  label: string;
  status: string;
  discipline?: string;
}

export interface UnsignedPocSummary {
  id: string;
  label: string;
  status: string;
}

export interface PendingEvvVisitSummary {
  visitId: string;
  visitDate: string;
}

export interface DischargeReadiness {
  openPriorAuths: OpenPriorAuthSummary[];
  unsignedPocs: UnsignedPocSummary[];
  futureShiftCount: number;
  /** DSC-03 (Phase 10): EVV-required visits still Pending, not billable until reconciled */
  pendingEvvVisits: PendingEvvVisitSummary[];
  /** DSC-03 (Phase 11): completed visits not on a claim yet; informational, they stay billable */
  unbilledVisits: { count: number; oldestDateOfService: string | null };
}

/** YYYY-MM-DD for a Date in local time */
export const toIsoDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Start of a schedule entry as a Date (date 'YYYY-MM-DD' + startTime 'HH:mm' or an ISO timestamp). */
export const scheduleEntryStart = (entry: Pick<ScheduleEntry, 'date' | 'startTime'> & { startAt?: string | null }): Date | null => {
  const { date, startTime } = entry;
  if (entry.startAt) {
    const at = new Date(entry.startAt);
    if (!isNaN(at.getTime())) return at;
  }
  if (startTime && startTime.includes('T')) {
    const parsed = new Date(startTime);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  if (!date) return null;
  const day = date.slice(0, 10);
  const time = startTime && /^\d{1,2}:\d{2}/.test(startTime) ? startTime.slice(0, 5).padStart(5, '0') : '00:00';
  const parsed = new Date(`${day}T${time}:00`);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/** Count shifts that start after `now` and are not cancelled (HLD-02, DSC-03 read-only count). */
export const countFutureShiftsIn = (entries: ScheduleEntry[], now: Date = new Date()): number =>
  entries.filter((entry) => {
    if (String(entry.status || '').toLowerCase() === 'cancelled') return false;
    const start = scheduleEntryStart(entry);
    return start !== null && start.getTime() > now.getTime();
  }).length;

const isOpenPa = (auth: Authorization) => OPEN_PA_STATUSES.has(String(auth.status || '').toUpperCase());

export const isApprovedPa = (auth: Authorization) => String(auth.status || '').toUpperCase() === PAStatus.APPROVED;

/** Readable message for an Admit failure (422 GUARDIAN_CONSENT_REQUIRED / NO_ACCEPTED_REFERRAL, 409, 402 PATIENT_CAP_REACHED). */
export function admitErrorMessage(error: unknown): string {
  const code = error instanceof ApiError ? error.domainCode : undefined;
  switch (code) {
    case 'GUARDIAN_CONSENT_REQUIRED':
      return 'A guardian who can consent is required for patients under 18. Add one under Demographics → Guardians & legal representatives.';
    case 'NO_ACCEPTED_REFERRAL':
      return 'The patient has no accepted referral. Accept a referral before admitting.';
    case 'INVALID_TRANSITION':
      return 'Only patients pending admission can be admitted. Refresh the chart and try again.';
    case 'PATIENT_CAP_REACHED':
      return capReachedMessage(error instanceof ApiError ? error.details : undefined);
    case 'ACCOUNT_READ_ONLY':
    case 'ACCOUNT_SUSPENDED':
    case 'ACCOUNT_CLOSED':
      return describeApiError(error);
    default:
      return error instanceof Error && error.message ? error.message : 'Unable to admit the patient.';
  }
}

export class PatientLifecycleService {
  /**
   * Admit a pending patient (SOC-05). An incomplete SOC checklist never blocks; it comes back
   * as warnings. 422 GUARDIAN_CONSENT_REQUIRED / NO_ACCEPTED_REFERRAL throw an ApiError.
   */
  static async admit(patientId: string, socDate: string): Promise<AdmitPatientResponse> {
    const response: ApiResponse<Partial<AdmitPatientResponse>> = await apiClient.post(
      `/patientmr/${patientId}/admit`,
      { socDate }
    );
    if (response.success) {
      const data = response.data ?? {};
      return {
        patient: data.patient,
        warnings: Array.isArray(data.warnings) ? (data.warnings as AdmitWarning[]) : [],
        activatedDisciplines: Array.isArray(data.activatedDisciplines) ? data.activatedDisciplines : [],
      };
    }
    throw new Error(response.error?.message || 'Failed to admit the patient');
  }

  /** Number of future, non-cancelled shifts for the patient. Read-only; nothing is cancelled. */
  static async countFutureShifts(patientId: string, now: Date = new Date()): Promise<number> {
    const end = new Date(now);
    end.setDate(end.getDate() + FUTURE_SHIFT_WINDOW_DAYS);
    const result = await listScheduleEntriesByPatient(patientId, {
      startDate: toIsoDate(now),
      endDate: toIsoDate(end),
    });
    return countFutureShiftsIn(result.items || [], now);
  }

  /** All PAs for the patient (used for the hold PA-review banner and the discharge panel). */
  static async listPriorAuths(patientId: string): Promise<Authorization[]> {
    return AuthorizationsService.getAuthorizations(patientId);
  }

  /**
   * Discharge readiness (DSC-03): open PAs, unsigned POC certifications, the future-shift count
   * and (Phase 10) visits with pending EVV. Unbilled visits are Phase 11.
   * Each source fails independently so one outage does not hide the others.
   */
  static async getDischargeReadiness(patientId: string): Promise<DischargeReadiness & { errors: string[] }> {
    const errors: string[] = [];
    const [pas, pocs, shifts, pendingEvv, unbilled] = await Promise.all([
      this.listPriorAuths(patientId).catch(() => {
        errors.push('prior authorizations');
        return [] as Authorization[];
      }),
      POCCertificationsService.listPOCCertifications(patientId)
        .then((r) => r.certifications || [])
        .catch(() => {
          errors.push('POC certifications');
          return [];
        }),
      this.countFutureShifts(patientId).catch(() => {
        errors.push('scheduled shifts');
        return 0;
      }),
      EvvReconciliationService.getPatientPendingEvv(patientId)
        .then((r) => r.visits)
        .catch(() => {
          errors.push('pending EVV visits');
          return [] as PatientPendingEvv['visits'];
        }),
      ClaimsService.getUnbilledVisits(patientId)
        .then((r) => ({ count: r.unbilledCount, oldestDateOfService: r.oldestDateOfService }))
        .catch(() => {
          errors.push('unbilled visits');
          return { count: 0, oldestDateOfService: null };
        }),
    ]);

    return {
      openPriorAuths: pas.filter(isOpenPa).map((pa) => ({
        id: String(pa.authorizationId ?? pa.id),
        label: pa.paNumber || pa.authNumber || 'Prior authorization',
        status: String(pa.status),
        discipline: pa.discipline ? String(pa.discipline) : undefined,
      })),
      unsignedPocs: pocs
        .filter((poc) => UNSIGNED_POC_STATUSES.has(String(poc.status || '').toLowerCase()))
        .map((poc) => ({
          id: String(poc.pocCertificationId ?? poc.id ?? ''),
          label: `${poc.discipline || 'POC'} ${poc.certificationPeriodStart || ''}–${poc.certificationPeriodEnd || ''}`.trim(),
          status: String(poc.status),
        })),
      futureShiftCount: shifts,
      pendingEvvVisits: pendingEvv.map((v) => ({ visitId: v.visitId, visitDate: v.visitDate })),
      unbilledVisits: unbilled,
      errors,
    };
  }
}

export default PatientLifecycleService;
